import React, { useEffect, useState } from 'react'
import { components } from 'react-select'

import Box from '../../Box'
import Icon from '../../Icon'

import { isNull, isUndefined } from '../../../utils/locDash'
import {
  getCascaderOptionLabel,
  getCascaderOptionValue,
  getInitialCascaderMenuOptions,
  parseOptions,
  toOption,
} from '../utils'
import { StyledCascaderOption, StyledOptionText } from '../styled'

import { MenuListProps, Option, MenuOptions } from '../Select.types'

const CascaderMenuList = (props: MenuListProps) => {
  const [selectedOption, setSelectedOption] = useState<MenuOptions>()

  const options = props.options.reduce(
    (acc: MenuOptions, item: Option<string>, itemIndex: number) => {
      if (item.options) {
        const { options: items } = item
        const groupId = `group-${itemIndex}`
        const children = items
          .map((child, index) => parseOptions(acc, child, `${itemIndex}-${index}`, groupId))
          .filter(Boolean)

        // difficult to reproduce
        // istanbul ignore next
        if (children.length) {
          acc.push({
            type: 'group',
            key: groupId,
            data: item,
            options: children,
            children: item.label,
            isSelected: false,
            isDisabled: false,
          })
        }
      } else {
        const option = toOption(item, `${itemIndex}`)
        // difficult to reproduce
        // istanbul ignore next
        if (option) {
          acc.push(option)
        }
      }
      return acc
    },
    [],
  ) as MenuOptions

  // difficult to reproduce
  // istanbul ignore next
  const setValue = (prevData: MenuOptions) => {
    const data = prevData
      .map((value) => value?.find(({ isSelected }) => isSelected === true)?.data)
      .filter(Boolean)

    const label = getCascaderOptionLabel(data)
    const value = getCascaderOptionValue(data)

    props.setValue({ label, value }, 'select-option')
  }

  // difficult to reproduce
  // istanbul ignore next
  const generateOptions = (
    prevData: MenuOptions,
    currentData: Option<string>,
    index: number,
  ): MenuOptions => {
    const clonePrevData = [...prevData]
    const nextData = clonePrevData[index + 1]

    // difficult to reproduce
    // istanbul ignore next
    if (currentData.isSelected) return prevData

    clonePrevData[index] = clonePrevData[index].map((value) => {
      return {
        ...value,
        // difficult to reproduce
        // istanbul ignore next
        isSelected: value?.key === currentData?.key,
				isDisabled: value?.key === currentData?.key ? false : value?.isDisabled,
      }
    })

    if (isUndefined(nextData) && currentData.type === 'group') {
      return [...clonePrevData, currentData.options]
    } else if (!isUndefined(nextData)) {
      clonePrevData.splice(index + 1, clonePrevData.length, currentData.options)
    }

    if (currentData.type === 'option') {
      setValue(clonePrevData)
    }

    return clonePrevData.filter(Boolean)
  }

  const onSelectOption = (data: Option<string>, index: number) => {
		if(data.isDisabled) return

    props.onSelect(data.data)
    setSelectedOption((prevData) => generateOptions(prevData, data, index))
  }

  useEffect(() => {
		const { selectProps: { value }, disabledLevel } = props;
    if (isNull(value)) return setSelectedOption([options])

    const { value: selectedOptionIds } = value as Option<Array<string>>
    setSelectedOption(getInitialCascaderMenuOptions(options, selectedOptionIds, disabledLevel))
  }, [])

  //  difficult to reproduce
  // istanbul ignore next
  return (
    <>
      {selectedOption?.map((option, index) => (
        <components.MenuList {...props} key={index}>
          {option?.map((value) => {
            return (
              <StyledCascaderOption
                key={value.key}
                onClick={() => onSelectOption(value, index)}
                className={value.isSelected && 'select__cascader__option--is-focused'}
                isSelected={value.isSelected}
								isDisabled={value.isDisabled}
              >
                <StyledOptionText as='span' title={value.data.label}>
                  {value.children}
                </StyledOptionText>
                {value.type === 'group' && (
                  <Box width={20}>
                    <Icon
                      name='Right-outline'
                      color={value.isSelected ? 'blue50' : 'black50'}
                      size={20}
                    />
                  </Box>
                )}
              </StyledCascaderOption>
            )
          })}
        </components.MenuList>
      ))}
    </>
  )
}

export default React.memo(CascaderMenuList)
